import { useCallback, useEffect, useState } from "react";
import { Plus, Trash2, FileText, UserCheck, Building2, Ruler } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "../lib/api";
import { inr, fdate, currentMonth } from "../lib/format";
import { PageHeader, ConfirmDialog, DataTable, StatCard, inputCls } from "../components/common";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../context/AuthContext";
import PaymentReceiptModal from "../components/PaymentReceiptModal";

const MODES = ["Cash", "UPI", "Bank Transfer", "Cheque", "Card"];

export default function Payments() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const [payments, setPayments] = useState([]);
  const [sales, setSales] = useState([]);
  const [month, setMonth] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [del, setDel] = useState(null);
  const [receiptModal, setReceiptModal] = useState({ open: false, paymentId: null });

  // Add Payment Modal State
  const [payDate, setPayDate] = useState(new Date().toISOString().slice(0, 10));
  const [payCustomer, setPayCustomer] = useState("");
  const [payAgent, setPayAgent] = useState("");
  const [payPlotSize, setPayPlotSize] = useState("");
  const [payAmount, setPayAmount] = useState("");
  const [payMode, setPayMode] = useState("UPI");
  const [payReference, setPayReference] = useState("");
  const [payReceivedBy, setPayReceivedBy] = useState("Admin");
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(() => {
    api.get(`/payments${month ? `?month=${month}` : ""}`).then((r) => setPayments(r.data));
    api.get("/sales").then((r) => setSales(r.data.filter((s) => s.status !== "cancelled")));
  }, [month]);

  useEffect(() => {
    load();
  }, [load]);

  const total = payments.reduce((s, p) => s + (p.amount || 0), 0);
  const monthTotal = payments.filter((p) => p.month === currentMonth()).reduce((s, p) => s + (p.amount || 0), 0);
  const pending = sales.reduce((s, x) => s + (x.balance || 0), 0);

  const handleOpenAddModal = () => {
    setPayDate(new Date().toISOString().slice(0, 10));
    setPayCustomer("");
    setPayAgent("");
    setPayPlotSize("");
    setPayAmount("");
    setPayMode("UPI");
    setPayReference("");
    setPayReceivedBy("Admin");
    setFormOpen(true);
  };

  const handleSavePayment = async (e) => {
    e.preventDefault();
    if (!payCustomer.trim()) {
      toast.error("Please enter or select a customer name");
      return;
    }
    const amt = parseFloat(payAmount);
    if (!amt || amt <= 0) {
      toast.error("Please enter a valid payment amount");
      return;
    }

    setSubmitting(true);
    try {
      await api.post("/payments", {
        date: payDate,
        customer_name: payCustomer.trim(),
        agent_name: payAgent.trim() || "Staff",
        plot_size: payPlotSize.trim() || "200 sq.yds",
        amount: amt,
        mode: payMode || "UPI",
        reference: payReference || "",
        received_by: payReceivedBy || "Admin",
      });
      toast.success("Payment recorded successfully");
      setFormOpen(false);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="payments-page">
      <PageHeader title="Payments & Collections" sub="Customer payment receipts and accounts ledger" testid="payments-header">
        <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className={inputCls + " w-auto"} data-testid="payments-month-filter" />
        {isAdmin && (
          <Button className="bg-emerald-700 hover:bg-emerald-800" onClick={handleOpenAddModal} data-testid="add-payment-button">
            <Plus className="mr-1 h-4 w-4" /> Add Payment
          </Button>
        )}
      </PageHeader>

      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatCard label="Total Collection" value={inr(total)} testid="payments-total" sub={month ? "Filtered view" : "All time"} />
        <StatCard label="This Month" value={inr(monthTotal)} tone="green" testid="payments-month-total" />
        <StatCard label="Pending Collection" value={inr(pending)} tone="amber" testid="payments-pending" sub="Outstanding on active bookings" />
      </div>

      <DataTable
        testid="payments-table"
        columns={[
          {
            key: "date",
            label: "Date",
            render: (r) => <span className="font-semibold text-slate-900 whitespace-nowrap">{fdate(r.date)}</span>,
          },
          {
            key: "agent_name",
            label: "Agent / Staff Name",
            render: (r) => (
              <div>
                <div className="font-medium text-slate-800">{r.agent_name || "Staff"}</div>
                {r.agent_code && <div className="text-[11px] font-mono text-slate-400">{r.agent_code}</div>}
              </div>
            ),
          },
          {
            key: "customer_name",
            label: "Customer Name",
            render: (r) => (
              <div>
                <div className="font-bold text-slate-900">{r.customer_name}</div>
                {r.customer_mobile && <div className="text-[11px] text-slate-500 font-mono">{r.customer_mobile}</div>}
              </div>
            ),
          },
          {
            key: "plot_size",
            label: "Plot Size",
            render: (r) => (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-emerald-50 text-emerald-800 border border-emerald-200 whitespace-nowrap">
                {r.plot_size || "200 sq.yds"}
              </span>
            ),
          },
          {
            key: "project_name",
            label: "Project & Plot",
            render: (r) => (
              <div>
                <div className="font-medium text-slate-800">{r.project_name || "-"}</div>
                {r.plot_number && <div className="text-xs font-bold text-emerald-700 font-mono">#{r.plot_number}</div>}
              </div>
            ),
          },
          {
            key: "amount",
            label: "Amount",
            align: "right",
            render: (r) => <span className="font-bold font-num text-slate-900 text-sm">{inr(r.amount)}</span>,
          },
          {
            key: "mode",
            label: "Mode",
            render: (r) => (
              <span className="inline-block rounded bg-slate-100 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-slate-700">
                {r.mode}
              </span>
            ),
          },
          {
            key: "actions",
            label: "Receipt / Actions",
            align: "right",
            render: (r) => (
              <div className="flex items-center justify-end gap-1">
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 gap-1 text-xs text-slate-700 hover:bg-slate-100"
                  onClick={() => setReceiptModal({ open: true, paymentId: r.id })}
                >
                  <FileText className="h-3.5 w-3.5 text-emerald-600" /> Receipt
                </Button>
                {isAdmin && (
                  <Button
                    size="sm"
                    variant="ghost"
                    aria-label="Delete payment"
                    className="h-8 w-8 p-0 text-red-600 hover:bg-red-50 hover:text-red-700"
                    data-testid={`delete-payment-${r.id}`}
                    onClick={() => setDel(r)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            ),
          },
        ]}
        rows={payments}
      />

      {/* Official Payment Receipt Modal */}
      <PaymentReceiptModal
        paymentId={receiptModal.paymentId}
        open={receiptModal.open}
        onOpenChange={(open) => setReceiptModal({ open, paymentId: null })}
      />

      {/* Enhanced Add Payment Modal */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="payment-form">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Record Payment</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSavePayment} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Date */}
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Date *
                </Label>
                <Input
                  type="date"
                  className="mt-1.5"
                  value={payDate}
                  onChange={(e) => setPayDate(e.target.value)}
                  required
                  data-testid="payment-form-field-date"
                />
              </div>

              {/* Agent / Staff Name */}
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Agent / Staff Name *
                </Label>
                <Input
                  type="text"
                  className="mt-1.5"
                  value={payAgent}
                  onChange={(e) => setPayAgent(e.target.value)}
                  placeholder="e.g. Rajesh Kumar or Staff Name"
                  required
                  autoComplete="off"
                  data-testid="payment-form-field-agent_name"
                />
              </div>

              {/* Customer Name */}
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Customer Name *
                </Label>
                <Input
                  type="text"
                  className="mt-1.5"
                  value={payCustomer}
                  onChange={(e) => setPayCustomer(e.target.value)}
                  placeholder="e.g. Ramesh Chandra"
                  required
                  autoComplete="off"
                  data-testid="payment-form-field-customer_name"
                />
              </div>

              {/* Plot Size */}
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Plot Size *
                </Label>
                <Input
                  type="text"
                  className="mt-1.5"
                  value={payPlotSize}
                  onChange={(e) => setPayPlotSize(e.target.value)}
                  placeholder="e.g. 200 sq.yds"
                  required
                  autoComplete="off"
                  data-testid="payment-form-field-plot_size"
                />
              </div>

              {/* Amount */}
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Amount (₹) *
                </Label>
                <Input
                  type="number"
                  step="any"
                  className="mt-1.5 font-bold"
                  value={payAmount}
                  onChange={(e) => setPayAmount(e.target.value)}
                  placeholder="0.00"
                  required
                  data-testid="payment-form-field-amount"
                />
              </div>

              {/* Payment Mode */}
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Payment Mode
                </Label>
                <select
                  className={`${inputCls} mt-1.5`}
                  value={payMode}
                  onChange={(e) => setPayMode(e.target.value)}
                  data-testid="payment-form-field-mode"
                >
                  {MODES.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </select>
              </div>

              {/* Reference / UTR */}
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Reference / UTR
                </Label>
                <Input
                  type="text"
                  className="mt-1.5"
                  value={payReference}
                  onChange={(e) => setPayReference(e.target.value)}
                  placeholder="e.g. UPI / Cheque No / NEFT ref"
                  autoComplete="off"
                  data-testid="payment-form-field-reference"
                />
              </div>

              {/* Received By */}
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Received By
                </Label>
                <Input
                  type="text"
                  className="mt-1.5"
                  value={payReceivedBy}
                  onChange={(e) => setPayReceivedBy(e.target.value)}
                  placeholder="e.g. Admin or Cashier"
                  autoComplete="off"
                  data-testid="payment-form-field-received_by"
                />
              </div>
            </div>

            <DialogFooter className="mt-4 gap-2">
              <Button type="button" variant="outline" onClick={() => setFormOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} className="bg-emerald-700 hover:bg-emerald-800">
                {submitting ? "Saving..." : "Save Payment"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={!!del}
        onOpenChange={() => setDel(null)}
        title="Delete Payment"
        message={`Delete payment of ${inr(del?.amount)} from ${del?.customer_name}? Sale balance will be updated.`}
        onConfirm={async () => {
          await api.delete(`/payments/${del.id}`);
          toast.success("Payment deleted");
          load();
        }}
        testid="payment-delete-confirm"
      />
    </div>
  );
}
